import { Component, Input, forwardRef, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../services/global-environment';
import { Subject } from 'rxjs';
import { takeUntil, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { I18nService } from '../../services/i18n.service';
import { AuthService } from '../../services/auth.service';
import { AssistantUrlService } from '../../services/assistant-url.service';

interface ApiOption {
  value: string;
  label: string;
}

interface ApiResponse {
  success: boolean;
  data: ApiOption[];
}

@Component({
  selector: 'app-form-dynamic-api-select',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  template: `
    <div class="form-section">
      <label [attr.for]="id" [class.required-field]="required">{{ label }}</label>
      
      <!-- 直接顯示下拉選單 -->
      <div>
        <select
          [id]="id"
          [name]="name"
          [ngModel]="value"
          (ngModelChange)="onSelectionChange($event)"
          (blur)="onTouched()"
          [disabled]="loading || hasError"
          class="form-input"
        >
          <option value="" disabled>
            {{ loading ? ('form.dynamic_api_select.loading' | translate) : 
               hasError ? ('form.dynamic_api_select.load_failed' | translate) : 
               getPlaceholder() }}
          </option>
          <option *ngFor="let option of options" [value]="option.value">
            {{ option.label }}
          </option>
        </select>
        <div *ngIf="hasError" class="error-message">
          <i class="fas fa-exclamation-triangle"></i>
          {{ 'form.dynamic_api_select.load_options_failed' | translate }}
        </div>
        <div *ngIf="value" class="selected-value-display">
          <small class="text-muted">
            <i class="fas fa-info-circle"></i>
            {{ 'form.dynamic_api_select.selected_value' | translate }}<span class="value-text">{{ value }}</span>
          </small>
        </div>
      </div>

      <!-- 除錯面板 - 顯示給開發者看 -->
      <!-- <div *ngIf="editMode && false" class="debug-panel" style="border: 1px solid #ff9800; padding: 10px; margin-top: 10px; font-size: 12px; background: #fff3e0; border-radius: 4px;">
        <strong style="color: #e65100;">🔍 除錯資訊 (編輯模式)</strong><br>
        <div style="margin-top: 8px; line-height: 1.6;">
          <div style="padding: 4px; background: #fff; border: 1px solid #ff9800; border-radius: 3px; margin-bottom: 8px;">
            <strong style="color: #d84315;">🎯 關鍵值狀態：</strong><br>
            <strong>當前值 (this.value):</strong> <code style="background: {{ value ? '#c5e1a5' : '#ffcdd2' }}; padding: 2px 4px;">{{ value || '❌ (空)' }}</code><br>
            <strong>值的長度:</strong> {{ value ? value.length : 0 }}<br>
            <strong>值的類型:</strong> {{ getValueType() }}<br>
          </div>
          <strong>原始值 (originalValue):</strong> <code style="background: #ffecb3; padding: 2px 4px; font-size: 10px;">{{ originalValue | json }}</code><br>
          <strong>初始顯示值:</strong> <code style="background: #ffecb3; padding: 2px 4px;">{{ initialDisplayValue || '(空)' }}</code><br>
          <strong>選項數量:</strong> {{ options.length }}<br>
          <strong>載入狀態:</strong> {{ loading ? '載入中...' : '已完成' }}<br>
          <strong>錯誤狀態:</strong> {{ hasError ? '有錯誤' : '正常' }}<br>
          <strong style="color: #d84315;">⚙️ API 設定:</strong><br>
          <strong>Form ID:</strong> <code style="background: #c5e1a5; padding: 1px 3px;">{{ formId || '(未設定)' }}</code><br>
          <strong>Label Field:</strong> <code style="background: #c5e1a5; padding: 1px 3px;">{{ labelField || '(未設定)' }}</code><br>
          <strong>Value Field:</strong> <code style="background: #c5e1a5; padding: 1px 3px;">{{ valueField || '(未設定)' }}</code><br>
          <div *ngIf="options.length > 0" style="margin-top: 8px; border-top: 1px solid #ffcc80; padding-top: 8px;">
            <strong>所有選項 (共 {{ options.length }} 個)：</strong><br>
            <div style="max-height: 200px; overflow-y: auto; margin-top: 4px; padding: 4px; background: white; border: 1px solid #ffcc80; border-radius: 4px;">
              <div *ngFor="let opt of options; let i = index" style="margin: 2px 0; padding: 2px 4px; border-bottom: 1px solid #ffe0b2;">
                <strong>{{ i + 1 }}.</strong> 
                value: <code style="background: #c5e1a5; padding: 1px 3px; font-size: 11px;">{{ opt.value }}</code> | 
                label: <code style="background: #b3e5fc; padding: 1px 3px; font-size: 11px;">{{ opt.label }}</code>
              </div>
            </div>
            <div *ngIf="value && !isValueInOptions()" style="margin-top: 8px; padding: 8px; background: #ffebee; border: 1px solid #ef5350; border-radius: 4px;">
              <strong style="color: #c62828;">⚠️ 問題診斷：</strong><br>
              <div style="margin-top: 4px; color: #d32f2f;">
                當前值 <code>{{ value }}</code> 不在選項列表中！<br>
                <strong>可能原因：</strong><br>
                1. Value Field 設定錯誤（目前是: <code>{{ valueField }}</code>）<br>
                2. API 返回的資料欄位名稱不匹配<br>
                3. 資料格式不一致（GUID vs 欄位名稱）<br>
                <strong>建議：</strong>檢查表單設計器中的 Value Field 設定
              </div>
            </div>
          </div>
        </div>
      </div> -->
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormDynamicApiSelectComponent),
      multi: true
    }
  ],
  styleUrls: ['total-component.scss']
})
export class FormDynamicApiSelectComponent implements ControlValueAccessor, OnInit, OnDestroy, OnChanges {
  @Input() label: string = '';
  @Input() id: string = '';
  @Input() name: string = '';
  @Input() placeholder: string = '';
  @Input() required: boolean = false;
  @Input() formId: string = '';
  @Input() labelField: string = '';
  @Input() valueField: string = '';
  @Input() enableWhereCondition: boolean = false;
  @Input() whereSourceComponent: string = '';
  @Input() whereTargetField: string = '';
  @Input() editMode: boolean = false; // 新增：是否為編輯模式

  value: any;
  options: ApiOption[] = [];
  loading: boolean = false;
  hasError: boolean = false;
  
  // 保留原始值，用於判斷是否有修改
  originalValue: any = null;
  initialDisplayValue: string = '';

  private destroy$ = new Subject<void>();
  private apiUrl = environment.apiUrl;
  private cache: Map<string, ApiOption[]> = new Map();

  // ControlValueAccessor callback 函數
  onChange = (value: any) => {};
  onTouched = () => {};

  // Contractor 角色相關屬性
  private userRoles: string[] = [];
  private currentUserId: string = '';

  constructor(
    private http: HttpClient, 
    private i18nService: I18nService, 
    private authService: AuthService,
    private assistantUrlService: AssistantUrlService
  ) {}

  getPlaceholder(): string {
    return this.placeholder || this.i18nService.translate('form.dynamic_api_select.please_select');
  }
  
  // 檢查當前值是否在選項列表中
  isValueInOptions(): boolean {
    if (!this.value || !this.options || this.options.length === 0) {
      return true; // 沒有值或沒有選項時不顯示錯誤
    }
    return this.options.some(opt => opt.value === this.value);
  }
  
  // 取得值的類型（用於除錯）
  getValueType(): string {
    if (this.value === null) return 'null';
    if (this.value === undefined) return 'undefined';
    if (this.value === '') return 'empty string';
    return typeof this.value;
  }

  ngOnInit(): void {
    // 初始化使用者角色資訊
    this.initializeUserInfo();

    // 只有在有必要的配置時才載入選項
    if (this.formId && this.labelField && this.valueField) {
      this.loadOptions();
    }

    // 如果啟用了 where 條件，設定來源元件監聽器
    if (this.enableWhereCondition) {
      this.setupSourceComponentListener();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // 當 formId、labelField 或 valueField 變更時，重新載入選項
    if ((changes['formId'] && !changes['formId'].firstChange && changes['formId'].currentValue) ||
        (changes['labelField'] && !changes['labelField'].firstChange && changes['labelField'].currentValue) ||
        (changes['valueField'] && !changes['valueField'].firstChange && changes['valueField'].currentValue)) {
      this.loadOptions();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * 初始化使用者資訊
   */
  private async initializeUserInfo(): Promise<void> {
    try {
      // 取得使用者資訊
      const userInfo = this.authService.getUserInfo();
      this.currentUserId = userInfo?.userid || '';
      
      // 取得使用者角色（使用 AssistantUrlService 取得正確的 assistantNo）
      const assistantNo = this.assistantUrlService.getCurrentAssistantId();
      this.userRoles = this.authService.getUserRolesForModule(assistantNo);
      
      // 記錄使用者資訊
    } catch (error) {
      console.error('初始化使用者資訊失敗:', error);
      this.currentUserId = '';
      this.userRoles = [];
    }
  }

  /**
   * 檢查是否為 contractor 角色
   */
  private isContractorRole(): boolean {
    return this.userRoles.includes('contractor');
  }



  private async loadOptions(): Promise<void> {
    console.log('=== loadOptions called ===');
    console.log('Config:', {
      formId: this.formId,
      labelField: this.labelField,
      valueField: this.valueField,
      enableWhereCondition: this.enableWhereCondition,
      whereSourceComponent: this.whereSourceComponent,
      whereTargetField: this.whereTargetField,
      isContractor: this.isContractorRole(),
      currentValue: this.value,
      originalValue: this.originalValue
    });

    if (!this.formId || !this.labelField || !this.valueField) {
      console.warn('FormDynamicApiSelectComponent: formId, labelField and valueField are required', { formId: this.formId, labelField: this.labelField, valueField: this.valueField });
      return;
    }

    // 如果啟用了 where 條件，需要檢查相關設定
    if (this.enableWhereCondition) {
      if (!this.whereSourceComponent || !this.whereTargetField) {
        console.warn('FormDynamicApiSelectComponent: whereSourceComponent and whereTargetField are required when enableWhereCondition is true');
        return;
      }

      // 獲取來源元件的值
      const sourceValue = this.getSourceComponentValue();
      if (!sourceValue) {
        this.options = [];
        return;
      }
    }

    // 檢查快取 - 使用 companyid + formId + labelField + valueField + where條件 + contractor條件 作為快取鍵
    const companyid = environment.companyid;
    const whereCondition = this.enableWhereCondition ? `_${this.whereSourceComponent}_${this.whereTargetField}_${this.getSourceComponentValue()}` : '';
    const contractorCondition = this.isContractorRole() ? `_contractor_${this.currentUserId}` : '';
    
    const cacheKey = `${companyid}_${this.formId}_${this.labelField}_${this.valueField}${whereCondition}${contractorCondition}`;

    console.log('Cache key:', cacheKey);

    // if (this.cache.has(cacheKey)) {
    //   console.log('Using cached options');
    //   this.options = this.cache.get(cacheKey) || [];
    //   return;
    // }

    if (!companyid) {
      console.error('FormDynamicApiSelectComponent: companyid is not available in environment');
      this.hasError = true;
      return;
    }

    this.loading = true;
    this.hasError = false;
    this.options = [];

    console.log("開始呼叫API");
    
    const url = `${this.apiUrl}/api/dynamic-data/form-select-value`;
    const payload: any = {
      companyid: companyid,
      formid: this.formId,
      label: this.labelField,
      value: this.valueField
    };

    // 建立 whereConditions 陣列來處理多個篩選條件
    const whereConditions: any[] = [];

    // 如果啟用了 where 條件，添加原本的條件
    if (this.enableWhereCondition && this.whereSourceComponent && this.whereTargetField) {
      const sourceValue = this.getSourceComponentValue();
      if (sourceValue) {
        whereConditions.push({
          field: this.whereTargetField,
          value: sourceValue
        });
      }
    }

    // Contractor 角色的特殊處理 - 加入 created_by 條件
    if (this.isContractorRole() && this.currentUserId) {
      whereConditions.push({
        field: 'created_by',
        value: this.currentUserId
      });
      // Contractor 角色自動加入 created_by 篩選
    }

    // 如果有 whereConditions，加入到 payload 中
    if (whereConditions.length > 0) {
      if (whereConditions.length === 1) {
        payload.whereCondition = whereConditions[0];
      } else {
        // 多個條件的話，使用陣列格式
        payload.whereConditions = whereConditions;
      }
    }

    console.log('API URL:', url);
    console.log('API Payload:', JSON.stringify(payload, null, 2));

    // 額外確認 where 條件和 contractor 條件的詳細資訊
    if (this.enableWhereCondition || this.isContractorRole()) {
      console.log('=== CONDITION DEBUG ===');
      console.log('enableWhereCondition:', this.enableWhereCondition);
      console.log('whereSourceComponent:', this.whereSourceComponent);
      console.log('whereTargetField:', this.whereTargetField);
      console.log('sourceValue:', this.getSourceComponentValue());
      console.log('isContractorRole:', this.isContractorRole());
      console.log('currentUserId:', this.currentUserId);
      console.log('whereConditions:', whereConditions);
      console.log('payload.whereCondition:', payload.whereCondition);
      console.log('payload.whereConditions:', payload.whereConditions);
      console.log('======================');
    }

    this.http.post<ApiResponse>(url, payload)
      .pipe(
        takeUntil(this.destroy$),
        catchError(error => {
          console.error('Failed to load form container options:', error);
          this.hasError = true;
          return of({ success: false, data: [] });
        })
      )
      .subscribe(response => {
        console.log('API Response:', response);
        this.loading = false;

        if (response.success && Array.isArray(response.data)) {
          this.options = response.data;
          console.log('Loaded options:', this.options);
          console.log('First 3 options detail:', this.options.slice(0, 3));
          
          // 檢查當前值是否在選項中
          if (this.value) {
            console.log('Checking if current value exists in options:');
            console.log('Current value:', this.value);
            const foundOption = this.options.find(opt => opt.value === this.value);
            if (foundOption) {
              console.log('✅ Found matching option:', foundOption);
            } else {
              console.log('❌ No matching option found for value:', this.value);
              console.log('Available option values:', this.options.map(opt => opt.value));
              
              // 嘗試用 label 來找
              if (this.originalValue && this.originalValue.value && this.originalValue.value.value) {
                const labelToFind = this.originalValue.value.value;
                console.log('Trying to find by label:', labelToFind);
                const foundByLabel = this.options.find(opt => opt.label === labelToFind);
                if (foundByLabel) {
                  console.log('✅ Found by label! Setting value to:', foundByLabel.value);
                  this.value = foundByLabel.value;
                }
              }
            }
          }
          
          // 快取結果
          this.cache.set(cacheKey, this.options);
        } else {
          this.hasError = true;
          console.error('Invalid API response format:', response);
        }
      });
  }

  // 獲取來源元件的值
  public getSourceComponentValue(): string {
    if (!this.whereSourceComponent) {
      return '';
    }

    try {
      // 方法1: 嘗試從表單控制項中獲取值 (Angular Reactive Forms)
      const parentForm = document.querySelector('form');
      if (parentForm) {
        const sourceElement = parentForm.querySelector(`[name="${this.whereSourceComponent}"]`) as HTMLInputElement | HTMLSelectElement;
        if (sourceElement && sourceElement.value) {
          console.log('Found source element via form:', sourceElement, 'Value:', sourceElement.value);
          return sourceElement.value;
        }
      }

      // 方法2: 直接用 name 屬性查找
      const sourceElement = document.querySelector(`[name="${this.whereSourceComponent}"]`) as HTMLInputElement | HTMLSelectElement;
      if (sourceElement && sourceElement.value) {
        console.log('Found source element via name:', sourceElement, 'Value:', sourceElement.value);
        return sourceElement.value;
      }

      // 方法3: 用 id 屬性查找（備用）
      const sourceElementById = document.querySelector(`#${this.whereSourceComponent}`) as HTMLInputElement | HTMLSelectElement;
      if (sourceElementById && sourceElementById.value) {
        console.log('Found source element via id:', sourceElementById, 'Value:', sourceElementById.value);
        return sourceElementById.value;
      }

      console.warn('Source element not found:', this.whereSourceComponent);
      return '';
    } catch (error) {
      console.error('Error getting source component value:', error);
      return '';
    }
  }

  // 監聽來源元件的變化
  private setupSourceComponentListener(): void {
    if (!this.enableWhereCondition || !this.whereSourceComponent) {
      return;
    }

    console.log('Setting up source component listener for:', this.whereSourceComponent);

    // 設定一個定時器來監聽來源元件的變化
    const checkInterval = setInterval(() => {
      // 嘗試多種方式找到來源元件
      let sourceElement: HTMLInputElement | HTMLSelectElement | null = null;

      // 方法1: 從表單中查找
      const parentForm = document.querySelector('form');
      if (parentForm) {
        sourceElement = parentForm.querySelector(`[name="${this.whereSourceComponent}"]`) as HTMLInputElement | HTMLSelectElement;
      }

      // 方法2: 直接查找
      if (!sourceElement) {
        sourceElement = document.querySelector(`[name="${this.whereSourceComponent}"]`) as HTMLInputElement | HTMLSelectElement;
      }

      // 方法3: 用 id 查找
      if (!sourceElement) {
        sourceElement = document.querySelector(`#${this.whereSourceComponent}`) as HTMLInputElement | HTMLSelectElement;
      }

      if (sourceElement && !sourceElement.dataset['listenerAdded']) {
        console.log('Found source element, adding listener:', sourceElement);
        sourceElement.dataset['listenerAdded'] = 'true';

        // 添加多種事件監聽
        ['change', 'input', 'blur'].forEach(eventType => {
          sourceElement!.addEventListener(eventType, () => {
            // 使用 setTimeout 確保值已更新
            setTimeout(async () => {
              // 清除快取並重新載入選項
              this.cache.clear();
              await this.loadOptions();
            }, 10);
          });
        });

        clearInterval(checkInterval);
      }
    }, 100);

    // 10秒後停止檢查
    setTimeout(() => {
      clearInterval(checkInterval);
      console.log('Stopped checking for source component:', this.whereSourceComponent);
    }, 10000);
  }

  onSelectionChange(selectedValue: string): void {
    this.value = selectedValue;

    // 如果在編輯模式下，檢查是否選擇了原始值
    if (this.editMode && this.originalValue) {
      // 如果選擇了原始值（沒有改變）
      if (this.originalValue.type === 'dynamic-api-select' && 
          this.originalValue.value && 
          this.originalValue.value.key === selectedValue) {
        // 直接返回原始值的格式
        this.onChange(selectedValue);
        return;
      }
    }

    // 如果值有改變，使用新格式
    const selectedOption = this.options.find(option => option.value === selectedValue);

    if (selectedOption) {
      const optionValue = {
        type: 'form-container-select',
        value: selectedOption.label,
        id: selectedOption.value,
        label: selectedOption.label
      };
      this.onChange(optionValue);
    } else {
      const emptyValue = {
        type: 'form-container-select',
        value: '',
        id: '',
        label: ''
      };
      this.onChange(emptyValue);
    }
  }

  writeValue(value: any): void {
    // 處理從資料庫來的資料格式
    if (typeof value === 'object' && value !== null) {
      // 處理直接的 key/value 格式 { key: '...', value: '...' }
      if (value.key && value.value && !value.type) {
        this.value = value.key || '';
        this.originalValue = {
          type: 'dynamic-api-select',
          value: value
        };
        this.initialDisplayValue = value.value || '';
      }
      // 處理新格式 { type: 'dynamic-api-select', value: { key: '...', value: '...' } }
      else if (value.type === 'dynamic-api-select' && value.value) {
        this.value = value.value.key || '';
        this.originalValue = value;
        this.initialDisplayValue = value.value.value || '';
        
        // 在編輯模式下，確保選項載入並選中正確的值
        if (this.editMode) {
          // 如果還沒載入選項，現在載入
          if (!this.options.length && this.formId && this.labelField && this.valueField) {
            this.loadOptions().then(() => {
              // 檢查當前值是否存在於選項中
              const foundOption = this.options.find(opt => opt.value === this.value);
              if (!foundOption && this.value) {
                // 如果找不到，嘗試用 label 匹配
                if (this.originalValue && this.originalValue.value && this.originalValue.value.value) {
                  const labelToMatch = this.originalValue.value.value;
                  const foundByLabel = this.options.find(opt => opt.label === labelToMatch);
                  if (foundByLabel) {
                    this.value = foundByLabel.value;
                  }
                }
              }
              
              // 強制觸發變更檢測
              const tempValue = this.value;
              this.value = '';
              setTimeout(() => {
                this.value = tempValue;
              }, 10);
            });
          }
        }
      }
      // 處理舊格式 { type: 'form-container-select', id: '...', label: '...' }
      else if (value.type === 'form-container-select') {
        this.value = value.id || '';
        this.originalValue = value;
        this.initialDisplayValue = value.label || '';
      }
    } 
    // 處理純字串格式（舊格式）
    else if (typeof value === 'string' && value) {
      this.value = value;
      this.originalValue = value;
      
      // 在編輯模式下載入選項
      if (this.editMode && !this.options.length && this.formId && this.labelField && this.valueField) {
        this.loadOptions().then(() => {
          // 強制觸發變更檢測
          const tempValue = this.value;
          this.value = '';
          setTimeout(() => {
            this.value = tempValue;
          }, 0);
        });
      }
    } 
    else {
      this.value = value || '';
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // 可以在這裡處理禁用狀態
  }

  // 重新載入選項（如果需要的話）
  async reloadOptions(): Promise<void> {
    const companyid = environment.companyid;
    const cacheKey = `${companyid}_${this.formId}_${this.labelField}_${this.valueField}`;
    this.cache.delete(cacheKey);
    await this.loadOptions();
  }

  // 手動觸發重新載入選項（用於除錯）
  // public manualReload(): void {
  //   console.log('Manual reload triggered');
  //   this.cache.clear();
  //   this.loadOptions();
  // }

  // 取得當前除錯資訊
  public getDebugInfo(): any {
    return {
      enableWhereCondition: this.enableWhereCondition,
      whereSourceComponent: this.whereSourceComponent,
      whereTargetField: this.whereTargetField,
      sourceValue: this.getSourceComponentValue(),
      optionsCount: this.options.length,
      hasError: this.hasError,
      loading: this.loading
    };
  }

  // 測試 API 呼叫（用於除錯）
  // public testApiCall(): void {
  //   console.log('=== MANUAL API TEST ===');

  //   const companyid = environment.companyid;
  //   const url = `${this.apiUrl}/api/dynamic-data/form-select-value`;
  //   const payload: any = {
  //     companyid: companyid,
  //     formid: this.formId,
  //     label: this.labelField,
  //     value: this.valueField
  //   };

  //   // 如果啟用了 where 條件，添加到 payload 中
  //   if (this.enableWhereCondition && this.whereSourceComponent && this.whereTargetField) {
  //     const sourceValue = this.getSourceComponentValue();
  //     if (sourceValue) {
  //       payload.whereCondition = {
  //         field: this.whereTargetField,
  //         value: sourceValue
  //       };
  //     }
  //   }

  //   console.log('Test API URL:', url);
  //   console.log('Test API Payload:', JSON.stringify(payload, null, 2));

  //   this.http.post<ApiResponse>(url, payload)
  //     .pipe(
  //       takeUntil(this.destroy$),
  //       catchError(error => {
  //         console.error('Test API call failed:', error);
  //         return of({ success: false, data: [] });
  //       })
  //     )
  //     .subscribe(response => {
  //       console.log('Test API Response:', response);
  //       console.log('Test API Response Data Count:', response.data?.length || 0);
  //       if (response.data && response.data.length > 0) {
  //         console.log('First few items:', response.data.slice(0, 3));
  //       }
  //       console.log('=====================');
  //     });
  // }
}
